import { createFileRoute, useSearch, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { communityTrpc } from '@org/clients/community';
import {
  Skeleton,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Avatar,
  AvatarFallback,
  AvatarImage,
  useToast,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@org/ui';
import {
  FileWarning,
  MessageSquare,
  User,
  FileText,
  Clock,
  CheckCircle,
  XCircle,
  Eye,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { useState } from 'react';
import { formatDistanceToNow, format } from 'date-fns';
import { ResolveReportModal } from '@/components/resolve-report-modal';

type SearchParams = {
  status?: 'pending' | 'reviewed' | 'resolved' | 'dismissed';
  type?: 'post' | 'comment' | 'message' | 'user';
  reportId?: string;
};

export const Route = createFileRoute('/admin/moderation')({
  component: ModerationPage,
  validateSearch: (search: Record<string, unknown>): SearchParams => ({
    status: search.status as SearchParams['status'],
    type: search.type as SearchParams['type'],
    reportId: search.reportId as string | undefined,
  }),
});

const TARGET_TYPE_ICONS = {
  post: FileText,
  comment: MessageSquare,
  message: MessageSquare,
  user: User,
};

const STATUS_STYLES = {
  pending: { bg: 'bg-amber-100', text: 'text-amber-700', icon: Clock },
  reviewed: { bg: 'bg-blue-100', text: 'text-blue-700', icon: Eye },
  resolved: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle },
  dismissed: { bg: 'bg-muted', text: 'text-muted-foreground', icon: XCircle },
};

function ModerationPage() {
  const { t } = useTranslation('community');
  const search = useSearch({ from: '/admin/moderation' });
  const navigate = useNavigate();
  const { toast } = useToast();

  const [page, setPage] = useState(0);
  const [selectedReportId, setSelectedReportId] = useState<string | null>(
    search.reportId ?? null
  );
  const [showResolveModal, setShowResolveModal] = useState(false);

  const limit = 20;

  const { data, isLoading, refetch } = communityTrpc.moderation.listReports.useQuery({
    status: search.status,
    targetType: search.type,
    limit,
    offset: page * limit,
  });

  const { data: selectedReport, isLoading: loadingReport } =
    communityTrpc.moderation.getReport.useQuery(
      { reportId: selectedReportId! },
      { enabled: !!selectedReportId }
    );

  const markReviewed = communityTrpc.moderation.markReviewed.useMutation({
    onSuccess: () => {
      toast({ title: t('admin.moderation.markedReviewed') });
      refetch();
    },
    onError: (error) => {
      toast({ title: t('common.error'), description: error.message, variant: 'destructive' });
    },
  });

  const handleFilterChange = (key: 'status' | 'type', value: string | undefined) => {
    const newSearch = { ...search };
    if (value === 'all') {
      delete newSearch[key];
    } else {
      newSearch[key] = value as any;
    }
    navigate({ search: newSearch });
    setPage(0);
  };

  const handleSelectReport = (reportId: string) => {
    setSelectedReportId(reportId);
    navigate({ search: { ...search, reportId } });
  };

  const reports = data?.reports ?? [];
  const totalPages = Math.ceil((data?.total ?? 0) / limit);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">{t('admin.moderation.title')}</h1>
        <p className="text-muted-foreground">{t('admin.moderation.subtitle')}</p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <div className="w-40">
          <Select
            value={search.status ?? 'all'}
            onValueChange={(v) => handleFilterChange('status', v)}
          >
            <SelectTrigger>
              <SelectValue placeholder={t('admin.moderation.status')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('admin.moderation.allStatuses')}</SelectItem>
              <SelectItem value="pending">{t('admin.moderation.pending')}</SelectItem>
              <SelectItem value="reviewed">{t('admin.moderation.reviewed')}</SelectItem>
              <SelectItem value="resolved">{t('admin.moderation.resolved')}</SelectItem>
              <SelectItem value="dismissed">{t('admin.moderation.dismissed')}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="w-40">
          <Select
            value={search.type ?? 'all'}
            onValueChange={(v) => handleFilterChange('type', v)}
          >
            <SelectTrigger>
              <SelectValue placeholder={t('admin.moderation.type')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('admin.moderation.allTypes')}</SelectItem>
              <SelectItem value="post">{t('admin.moderation.typePosts')}</SelectItem>
              <SelectItem value="comment">{t('admin.moderation.typeComments')}</SelectItem>
              <SelectItem value="message">{t('admin.moderation.typeMessages')}</SelectItem>
              <SelectItem value="user">{t('admin.moderation.typeUsers')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Reports List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <FileWarning className="h-5 w-5" aria-hidden="true" />
                {t('admin.moderation.reports')}
              </span>
              <span className="text-sm font-normal text-muted-foreground">
                {t('admin.moderation.total', { count: data?.total ?? 0 })}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-3">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} className="h-16 w-full" />
                ))}
              </div>
            ) : reports.length === 0 ? (
              <div className="py-8 text-center text-muted-foreground">
                <FileWarning className="mx-auto h-12 w-12 opacity-50" aria-hidden="true" />
                <p className="mt-2">{t('admin.moderation.noReports')}</p>
              </div>
            ) : (
              <div className="space-y-2">
                {reports.map((report) => {
                  const Icon = TARGET_TYPE_ICONS[report.targetType];
                  const statusStyle = STATUS_STYLES[report.status];
                  const isSelected = selectedReportId === report.reportId;

                  return (
                    <button
                      key={report.reportId}
                      onClick={() => handleSelectReport(report.reportId)}
                      className={`w-full rounded-lg border p-3 text-left transition-colors ${
                        isSelected
                          ? 'border-primary bg-primary/5'
                          : 'hover:bg-muted/50'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-start gap-3">
                          <div className={`rounded-full p-2 ${statusStyle.bg}`}>
                            <Icon className={`h-4 w-4 ${statusStyle.text}`} aria-hidden="true" />
                          </div>
                          <div>
                            <p className="font-medium capitalize">
                              {report.targetType} - {report.reason.replace('_', ' ')}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {t('admin.moderation.by')} {report.reporter?.displayName || report.reporter?.firstName || 'Unknown'}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {formatDistanceToNow(new Date(report.createdAt), { addSuffix: true })}
                            </p>
                          </div>
                        </div>
                        <span className={`shrink-0 rounded-full px-2 py-1 text-xs font-medium ${statusStyle.bg} ${statusStyle.text}`}>
                          {report.status}
                        </span>
                      </div>
                    </button>
                  );
                })}

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between pt-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.max(0, p - 1))}
                      disabled={page === 0}
                    >
                      <ChevronLeft className="h-4 w-4" aria-hidden="true" />
                      {t('admin.moderation.previous')}
                    </Button>
                    <span className="text-sm text-muted-foreground">
                      {t('admin.moderation.page', { page: `${page + 1} / ${totalPages}` })}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => p + 1)}
                      disabled={page >= totalPages - 1}
                    >
                      {t('admin.moderation.next')}
                      <ChevronRight className="h-4 w-4" aria-hidden="true" />
                    </Button>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Report Details */}
        <Card>
          <CardHeader>
            <CardTitle>{t('admin.moderation.reportDetails')}</CardTitle>
          </CardHeader>
          <CardContent>
            {!selectedReportId ? (
              <div className="py-8 text-center text-muted-foreground">
                <Eye className="mx-auto h-12 w-12 opacity-50" />
                <p className="mt-2">{t('admin.moderation.selectReport')}</p>
              </div>
            ) : loadingReport ? (
              <div className="space-y-4">
                <Skeleton className="h-20 w-full" />
                <Skeleton className="h-32 w-full" />
                <Skeleton className="h-10 w-full" />
              </div>
            ) : selectedReport ? (
              <div className="space-y-6">
                {/* Reporter Info */}
                <div>
                  <h4 className="mb-2 text-sm font-medium text-muted-foreground">
                    {t('admin.moderation.reportedBy')}
                  </h4>
                  <div className="flex items-center gap-3">
                    <Avatar className="h-10 w-10">
                      <AvatarImage src={selectedReport.reporter?.profileImageUrl ?? undefined} />
                      <AvatarFallback>
                        {(selectedReport.reporter?.displayName || selectedReport.reporter?.firstName || 'U')[0]}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-medium">
                        {selectedReport.reporter?.displayName ||
                         [selectedReport.reporter?.firstName, selectedReport.reporter?.lastName].filter(Boolean).join(' ') ||
                         'Unknown User'}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {selectedReport.reporter?.email}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Report Details */}
                <div>
                  <h4 className="mb-2 text-sm font-medium text-muted-foreground">
                    {t('admin.moderation.reportInfo')}
                  </h4>
                  <div className="space-y-2 rounded-lg border p-3">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t('admin.moderation.type')}</span>
                      <span className="capitalize">{selectedReport.targetType}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t('admin.moderation.reason')}</span>
                      <span className="capitalize">{selectedReport.reason.replace('_', ' ')}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t('admin.moderation.status')}</span>
                      <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLES[selectedReport.status].bg} ${STATUS_STYLES[selectedReport.status].text}`}>
                        {selectedReport.status}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t('admin.moderation.reported')}</span>
                      <span>{format(new Date(selectedReport.createdAt), 'PPp')}</span>
                    </div>
                    {selectedReport.description && (
                      <div className="border-t pt-2">
                        <span className="text-muted-foreground">{t('admin.moderation.description')}</span>
                        <p className="mt-1">{selectedReport.description}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Reported Content */}
                <div>
                  <h4 className="mb-2 text-sm font-medium text-muted-foreground">
                    {t('admin.moderation.reportedContent')}
                  </h4>
                  <div className="rounded-lg border p-3">
                    {selectedReport.targetType === 'post' && selectedReport.target && (
                      <div>
                        <p className="font-medium">{(selectedReport.target as any).title}</p>
                        <p className="mt-1 text-sm text-muted-foreground line-clamp-3">
                          {(selectedReport.target as any).content}
                        </p>
                        <p className="mt-2 text-xs text-muted-foreground">
                          {t('admin.moderation.by')} {(selectedReport.target as any).author?.displayName || (selectedReport.target as any).author?.firstName}
                        </p>
                      </div>
                    )}
                    {selectedReport.targetType === 'comment' && selectedReport.target && (
                      <div>
                        <p className="text-sm">{(selectedReport.target as any).content}</p>
                        <p className="mt-2 text-xs text-muted-foreground">
                          {t('admin.moderation.by')} {(selectedReport.target as any).author?.displayName || (selectedReport.target as any).author?.firstName}
                        </p>
                      </div>
                    )}
                    {selectedReport.targetType === 'message' && selectedReport.target && (
                      <div>
                        <p className="text-sm">{(selectedReport.target as any).message}</p>
                        <p className="mt-2 text-xs text-muted-foreground">
                          {t('admin.moderation.by')} {(selectedReport.target as any).sender?.displayName || (selectedReport.target as any).sender?.firstName}
                        </p>
                      </div>
                    )}
                    {selectedReport.targetType === 'user' && selectedReport.target && (
                      <div className="flex items-center gap-3">
                        <Avatar className="h-10 w-10">
                          <AvatarImage src={(selectedReport.target as any).profileImageUrl} />
                          <AvatarFallback>
                            {((selectedReport.target as any).displayName || (selectedReport.target as any).firstName || 'U')[0]}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium">
                            {(selectedReport.target as any).displayName ||
                             [(selectedReport.target as any).firstName, (selectedReport.target as any).lastName].filter(Boolean).join(' ')}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {(selectedReport.target as any).email}
                          </p>
                        </div>
                      </div>
                    )}
                    {!selectedReport.target && (
                      <p className="text-muted-foreground">{t('admin.moderation.contentUnavailable')}</p>
                    )}
                  </div>
                </div>

                {/* Moderator Notes */}
                {selectedReport.moderatorNotes && (
                  <div>
                    <h4 className="mb-2 text-sm font-medium text-muted-foreground">
                      {t('admin.moderation.moderatorNotes')}
                    </h4>
                    <div className="rounded-lg border bg-muted/50 p-3">
                      <p className="text-sm">{selectedReport.moderatorNotes}</p>
                      {selectedReport.resolvedAt && (
                        <p className="mt-2 text-xs text-muted-foreground">
                          {t('admin.moderation.resolvedOn')} {format(new Date(selectedReport.resolvedAt), 'PPp')}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Actions */}
                {(selectedReport.status === 'pending' || selectedReport.status === 'reviewed') && (
                  <div className="flex gap-2 border-t pt-4">
                    {selectedReport.status === 'pending' && (
                      <Button
                        variant="outline"
                        onClick={() => markReviewed.mutate({ reportId: selectedReport.reportId })}
                        disabled={markReviewed.isPending}
                      >
                        <Eye className="mr-2 h-4 w-4" />
                        {t('admin.moderation.markReviewed')}
                      </Button>
                    )}
                    <Button
                      variant="default"
                      onClick={() => setShowResolveModal(true)}
                    >
                      <CheckCircle className="mr-2 h-4 w-4" />
                      {t('admin.moderation.resolve')}
                    </Button>
                  </div>
                )}
              </div>
            ) : null}
          </CardContent>
        </Card>
      </div>

      {/* Resolve Modal */}
      {selectedReport && (
        <ResolveReportModal
          isOpen={showResolveModal}
          onClose={() => setShowResolveModal(false)}
          report={selectedReport}
          onSuccess={() => {
            setShowResolveModal(false);
            refetch();
          }}
        />
      )}
    </div>
  );
}
